<script setup lang="ts">
import { Ref, computed, ref, watchEffect } from 'vue'

import { refDebounced } from '@vueuse/core'
import { DateTime } from 'luxon'

import InfiniteScrollWrapper from '../../../common/components/InfiniteScrollWrapper.vue'
import GroupSelect from '../../../groups/components/input/GroupSelect.vue'
import TagMultiselect from '../../../tags/components/inputs/TagMultiselect.vue'
import EmployeeSelectCard from '../cards/EmployeeSelectCard.vue'
import EmployeeSelectedCard from '../cards/EmployeeSelectedCard.vue'

import { useEligibleQueryInfinite } from '../../composables/useEmployeeQuery'
import { useEmployeeSelectDialog } from '../../composables/useEmployeeSelectDialog'

import { Group } from '../../../groups/models/group'
import { Site } from '../../../sites/models/site'
import { Tag, TagMatchMode } from '../../../tags/models/tag'
import { Employee } from '../../models/employee'

const { site } = defineProps<{
  site: Site
}>()

const {
  isShown,
  employees,
  disabledEmployeeIds,
  areaId,
  skillIds,
  jobRoleId,
  hide,
  confirm,
  cancel,
  addEmployees,
  removeEmployee,
  removeAll,
} = useEmployeeSelectDialog()

/** The most the eligible-employees endpoint returns in one page */
const ELIGIBLE_PER_PAGE = 100

// Every eligible person, not only the pages scrolled to so far (E2EF-01)
const selectAll = async () => {
  while (hasNextPage.value && !error.value) {
    await fetchNextPage()
  }
  if (error.value) return

  const eligibleEmployeeIds = options.value.filter(
    (employee) =>
      !disabledEmployeeIds.value.includes(employee.id) &&
      !employees.value.some((e) => e.id === employee.id),
  )

  addEmployees(eligibleEmployeeIds)
}

const handleSelect = (employees: Employee[]) => {
  confirm(employees, {
    tags: tags.value,
    tagMatchMode: tagMatchMode.value,
  })
}

const handleCancel = () => {
  cancel()
}

const enabled = computed(() => isShown.value)

const nameInput = ref('')
const name = refDebounced(nameInput, 500)

const group: Ref<Group | null> = ref(null)

const tags: Ref<Tag[]> = ref([])
const tagMatchMode: Ref<TagMatchMode> = ref('any')

const isTagMatchAll = ref(tagMatchMode.value === 'all')

watchEffect(() => {
  isTagMatchAll.value = tagMatchMode.value === 'all'
})

watchEffect(() => {
  tagMatchMode.value = isTagMatchAll.value ? 'all' : 'any'
})

// A page at a time as the list scrolls; a large site has hundreds of eligible people
const { data, error, isFetching, hasNextPage, fetchNextPage } =
  useEligibleQueryInfinite(
    () => site.id,
    {
      areaId,
      skillIds,
      jobRoleId,
      startDate: DateTime.now(),
      endDate: DateTime.now().plus({ days: 7 }),
      name,
      group,
      tags,
      tagMatchMode,
      perPage: ELIGIBLE_PER_PAGE,
    },
    { enabled },
  )

// Once each: until the API orders the list (E2EF-03) a later page can repeat someone
const options = computed<Employee[]>(() => {
  const byId = new Map<Employee['id'], Employee>()
  for (const page of data.value?.pages ?? []) {
    for (const employee of page.employees) {
      if (!byId.has(employee.id)) byId.set(employee.id, employee)
    }
  }
  return [...byId.values()]
})
// How many match the filters, loaded or not
const total = computed(() => data.value?.pages[0]?.pagination.total)
</script>

<template>
  <Dialog
    :visible="isShown"
    header="Select Employees"
    dismissable-mask
    modal
    class="h-screen w-full max-w-7xl md:w-3/4"
    :content-class="'h-full'"
    @update:visible="hide"
  >
    <div class="flex h-full flex-col border-y md:flex-row">
      <!-- Selected employees: a short strip above the list on phones, a side column from md -->
      <aside
        class="flex max-h-32 w-full shrink-0 flex-col overflow-y-auto border-b md:max-h-none md:w-64 md:border-r md:border-b-0"
      >
        <div role="list" class="grid grid-cols-1 gap-2 overflow-y-auto pr-3">
          <EmployeeSelectedCard
            v-for="employee in employees"
            :key="employee.id"
            :employee="employee"
            @remove-employee="(employee) => removeEmployee(employee)"
          />
        </div>
      </aside>

      <div class="flex min-h-0 min-w-0 grow flex-col pl-2 pt-2">
        <Toolbar>
          <template #start>
            <div class="flex flex-wrap items-center gap-2">
              <InputText
                v-model="nameInput"
                label="Name"
                placeholder="Name"
                size="small"
              />

              <div
                v-if="false"
                class="inline-flex gap-x-1 border px-2 py-1 border-surface"
              >
                <GroupSelect
                  v-model:selected="group"
                  :site-id="site.id"
                  class="h-fit"
                  size="small"
                  show-clear
                />

                <ToggleButton
                  v-model="isTagMatchAll"
                  on-label="All"
                  off-label="Any"
                  size="small"
                />
              </div>

              <div class="inline-flex gap-x-1 border px-2 py-1 border-surface">
                <TagMultiselect
                  v-model:selected="tags"
                  :site-id="site.id"
                  :type="'employee'"
                  class="w-full"
                  placeholder="Filter by Groups"
                  size="small"
                  show-clear
                />

                <ToggleButton
                  v-model="isTagMatchAll"
                  on-label="All"
                  off-label="Any"
                  size="small"
                  class="w-14"
                />
              </div>
            </div>
          </template>

          <template #end>
            <div class="flex items-center gap-2">
              <span
                v-if="total !== undefined"
                class="text-sm text-on-surface-muted"
                data-testid="eligible-total"
              >
                {{ total }} eligible
              </span>

              <Button
                label="Select All"
                :loading="isFetching"
                :severity="'secondary'"
                @click="selectAll"
              />

              <Button
                label="Clear"
                :loading="isFetching"
                :severity="'secondary'"
                @click="removeAll"
              />
            </div>
          </template>
        </Toolbar>

        <InfiniteScrollWrapper
          height-class="h-full"
          :is-empty="options.length === 0"
          :is-fetching="isFetching"
          :error="error?.message"
          :has-next-page="hasNextPage"
          @load-more="fetchNextPage()"
        >
          <ul
            role="list"
            class="grid grid-cols-2 gap-2 pr-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
          >
            <EmployeeSelectCard
              v-for="employee in options"
              :key="employee.id"
              :employee="employee"
              :selected="employees.some((item) => item.id === employee.id)"
              :disabled="disabledEmployeeIds.includes(employee.id)"
              @add-employee="(employee) => addEmployees([employee])"
              @remove-employee="(employee) => removeEmployee(employee)"
            />
          </ul>
        </InfiniteScrollWrapper>
      </div>
    </div>

    <template #footer>
      <Button
        label="Confirm"
        :loading="isFetching"
        severity="primary"
        @click="handleSelect(employees)"
      />

      <Button
        label="Cancel"
        :loading="isFetching"
        severity="secondary"
        @click="handleCancel"
      />
    </template>
  </Dialog>
</template>
